import { useMemo } from 'react';

/**
 * Custom Hook for Filter and Sort Logic
 * Handles parameter filtering and sorting without managing state
 */
const useFilterSort = ({
  parameters,
  applyCatalogFiltersLocally = true,
  searchTerm,
  selectedTags,
  selectedStations,
  selectedSensitivity,
  selectedOmopStatus,
  selectedSourceSystems,
  aiSuggestedParameterIds,
  setParameterIds,
  showOnlyActiveListParams,
  activeWorkingList,
  listComparisonFilter,
  orderBy,
  order,
  parameterLists,
  isParameterInActiveList,
}) => {
  /**
   * Get parameter list status for comparison mode
   */
  const getParameterListStatus = (parameterId) => {
    if (!parameterLists || activeWorkingList.length === 0) return 'none';

    if (activeWorkingList.length === 1) {
      const list = parameterLists.find(l => l.id === activeWorkingList[0]);
      return list && list.parameterIds.includes(parameterId) ? 'inList' : 'none';
    }

    if (activeWorkingList.length === 2) {
      const list1 = parameterLists.find(l => l.id === activeWorkingList[0]);
      const list2 = parameterLists.find(l => l.id === activeWorkingList[1]);
      const inList1 = list1 && list1.parameterIds.includes(parameterId);
      const inList2 = list2 && list2.parameterIds.includes(parameterId);

      if (inList1 && inList2) return 'both';
      if (inList1) return 'list1Only';
      if (inList2) return 'list2Only';
      return 'neither';
    }

    return 'none';
  };

  /**
   * Filter parameters based on all active filters
   */
  const filteredParameters = useMemo(() => {
    return parameters.filter(param => {
      const matchesSearch =
        !applyCatalogFiltersLocally ||
        param.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        param.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTags =
        !applyCatalogFiltersLocally ||
        selectedTags.length === 0 ||
        selectedTags.some(tag => param.tags.includes(tag));

      const matchesStation =
        !applyCatalogFiltersLocally ||
        selectedStations.length === 0 ||
        selectedStations.some(station => param.stations.includes(station));

      const matchesSensitivity =
        !applyCatalogFiltersLocally ||
        !selectedSensitivity ||
        selectedSensitivity === 'all' ||
        param.sensitivity === selectedSensitivity;

      const matchesOmopStatus =
        !applyCatalogFiltersLocally ||
        !selectedOmopStatus ||
        selectedOmopStatus === 'all' ||
        param.omopStatus === selectedOmopStatus;

      const matchesSourceSystem =
        !applyCatalogFiltersLocally ||
        selectedSourceSystems.length === 0 ||
        selectedSourceSystems.includes(param.sourceSystem);

      // AI suggested parameters and Set parameters filter
      const hasAiFilter = aiSuggestedParameterIds && aiSuggestedParameterIds.length > 0;
      const hasSetFilter = setParameterIds && setParameterIds.length > 0;
      const matchesAiOrSetFilter =
        (!hasAiFilter && !hasSetFilter) ||
        (hasAiFilter && aiSuggestedParameterIds.includes(param.id)) ||
        (hasSetFilter && setParameterIds.includes(param.id));

      // Active list filter
      const matchesActiveListFilter =
        !showOnlyActiveListParams ||
        (activeWorkingList.length > 0 && isParameterInActiveList(param.id));

      // List comparison filter
      let matchesComparisonFilter = true;
      if (activeWorkingList.length === 2 && listComparisonFilter !== 'all') {
        const status = getParameterListStatus(param.id);
        matchesComparisonFilter = status === listComparisonFilter;
      }

      return (
        matchesSearch &&
        matchesTags &&
        matchesStation &&
        matchesSensitivity &&
        matchesOmopStatus &&
        matchesSourceSystem &&
        matchesAiOrSetFilter &&
        matchesActiveListFilter &&
        matchesComparisonFilter
      );
    });
  }, [
    parameters,
    applyCatalogFiltersLocally,
    searchTerm,
    selectedTags,
    selectedStations,
    selectedSensitivity,
    selectedOmopStatus,
    selectedSourceSystems,
    aiSuggestedParameterIds,
    setParameterIds,
    showOnlyActiveListParams,
    activeWorkingList,
    listComparisonFilter,
    isParameterInActiveList,
  ]);

  /**
   * Get sort value for a parameter
   */
  const getSortValue = (param) => {
    if (orderBy === 'lists') {
      const status = getParameterListStatus(param.id);
      return status === 'inList' ? 1 : 0;
    }
    if (orderBy === 'listA') {
      const status = getParameterListStatus(param.id);
      return status === 'list1Only' || status === 'both' ? 1 : 0;
    }
    if (orderBy === 'listB') {
      const status = getParameterListStatus(param.id);
      return status === 'list2Only' || status === 'both' ? 1 : 0;
    }
    return 0;
  };

  /**
   * Sort filtered parameters
   */
  const sortedParameters = useMemo(() => {
    if (!orderBy) return filteredParameters;

    return [...filteredParameters].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);

      if (order === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  }, [filteredParameters, orderBy, order]);

  /**
   * Get list comparison counts for UI display
   */
  const getListComparisonCounts = () => {
    if (!parameterLists || activeWorkingList.length !== 2) return null;

    const list1 = parameterLists.find(l => l.id === activeWorkingList[0]);
    const list2 = parameterLists.find(l => l.id === activeWorkingList[1]);

    if (!list1 || !list2) return null;

    const counts = {
      list1Only: 0,
      list2Only: 0,
      both: 0,
      neither: 0,
    };

    parameters.forEach(param => {
      const status = getParameterListStatus(param.id);
      if (status === 'list1Only') counts.list1Only++;
      else if (status === 'list2Only') counts.list2Only++;
      else if (status === 'both') counts.both++;
      else counts.neither++;
    });

    return counts;
  };

  return {
    filteredParameters,
    sortedParameters,
    getParameterListStatus,
    getListComparisonCounts,
  };
};

export default useFilterSort;
